import React, { useEffect, useMemo, useState } from 'react';
import { Box, CheckboxGroup, Tooltip } from '@chakra-ui/react';
import { Colors, Icon, Checkbox, Text, Button } from '@mercator-gcp/grout';
import Dropdown from 'components/elements/Dropdown';
import DropdownItem from 'components/elements/Dropdown/DropdownItem';
import ExclusionOptions from 'components/elements/Dropdown/ExclusionDropdown';
import {
  Option,
  OptionChild,
  Options,
} from 'components/elements/Dropdown/FilterDropdown/types';
import { PhaseGroupedOptions } from 'components/elements/Dropdown/PhaseGroupedOptions';
import PopoverComponent from 'components/elements/Dropdown/PopoverComponent';
import SearchInput from 'components/elements/SearchInput';
import { Spinner } from 'components/elements/Spinner';
import { FlexRow } from 'components/layout/FlexRow';
import { useDebounce } from 'hooks/useDebounced';
import { DEFAULT_VERTICAL_EXCLUSIONS } from 'utils/constants';

const noHeightButtonStyle = {
  height: 'auto',
};

const includeText = {
  justifyContent: 'space-between',
  textTransform: 'uppercase',
  fontSize: '10px',
  fontWeight: '600',
  color: Colors.ForestGreen900,
};

const descriptionText = {
  fontSize: '11px',
  color: 'brand.text.quiet',
};

const clearAllText = {
  fontSize: '11px',
  color: 'brand.tangerine.900',
  cursor: 'pointer',
};

const selectAllText = {
  pl: 'padding_8',
  pr: 'padding_8',
  pt: 'padding_4',
  position: 'sticky',
};

const optionContainer = {
  display: 'inline-flex',
  width: '100%',
  _hover: {
    background: 'brand.army-100',
  },
  _active: {
    background: 'brand.army-100',
  },
  pl: 'padding_12',
  pr: 'padding_12',
  pt: 'padding_4',
  pb: 'padding_4',
};

const checkboxStyle = {
  width: '100%',
};

const optionText = (hasChildren?: boolean) => ({
  letterSpacing: ' -0.13px',
  fontSize: '13px',
  color: 'brand.army-dark',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: hasChildren ? '150px' : '180px',
  width: '100%',
});

const panelBox = {
  justifyContent: 'space-between',
  display: 'flex',
  mb: (theme: any) => theme.space.eight,
  flexWrap: 'wrap',
  width: '100%',
};

const subOptionCheckboxGroup = {
  marginBottom: 'padding_8',
};

const checkboxContainerStyle = (maxHeight?: string) => ({
  maxHeight: maxHeight || 'auto',
  overflowY: maxHeight ? 'scroll' : 'auto',
});

const clearAllContainer = {
  mt: (theme: any) => theme.space.twelve,
  pl: (theme: any) => theme.space.twelve,
  pr: (theme: any) => theme.space.twelve,
};

const popOverRow = {
  display: 'inline-flex',
  justifyContent: 'space-between',
};

const exclusionRow = {
  display: 'flex',
  justifyContent: 'center',
  mt: (theme: any) => theme.space.eight,
  pt: (theme: any) => theme.space.eight,
};

const exclusionText = {
  color: 'brand.army-green',
  fontWeight: '700',
  fontSize: '11px',
  cursor: 'pointer',
};

const isOptionPartiallySelected = (option: Option, partiallySelectedValues: string[]) => {
  const childrenValues = option?.children?.map((child) => child.value) || [];

  const selectedChildValues = partiallySelectedValues?.filter((value: any) =>
    childrenValues?.includes(value),
  );

  return (
    selectedChildValues?.length > 0 &&
    selectedChildValues?.length < childrenValues?.length
  );
};

interface CheckboxOptionProps {
  option: Option;
  isPartiallySelected: boolean;
}

// Memoized so toggling one option doesn't re-render every other row's Chakra
// Tooltip (which is expensive). The inner Checkbox still updates its checked
// state through CheckboxGroup context; only the heavy Tooltip subtree is reused
// via its stable element reference.
const OptionCheckbox = React.memo(
  ({ option, isPartiallySelected }: CheckboxOptionProps) => {
    return (
      <Checkbox
        key={option.value}
        value={option.value}
        sx={checkboxStyle}
        isPartialCheck={isPartiallySelected}
      >
        <Tooltip label={option.label} isTruncated shouldWrapChildren>
          <Text as="div" sx={optionText(!!option.children)}>
            {option.label}
          </Text>
        </Tooltip>
      </Checkbox>
    );
  },
);

OptionCheckbox.displayName = 'OptionCheckbox';

interface SubOptionCheckboxProps {
  child: OptionChild;
}

const SubOptionCheckbox = ({ child }: SubOptionCheckboxProps) => {
  return (
    <Checkbox key={child.value} value={child.value} sx={optionContainer}>
      <Tooltip
        label={child.description ? child.description : child.label}
        shouldWrapChildren
      >
        <Text sx={optionText()}>{child.label}</Text>
      </Tooltip>
    </Checkbox>
  );
};

interface SubOptionCheckboxGroupProps {
  option: Option;
  childValues: any;
  isLoading: boolean;
  onCheckboxChange: (values: string[]) => void;
}

const SubOptionCheckboxGroup = ({
  option,
  childValues,
  isLoading,
  onCheckboxChange,
}: SubOptionCheckboxGroupProps) => {
  if (isLoading) return <Spinner />;

  return (
    <Box sx={subOptionCheckboxGroup}>
      <CheckboxGroup value={childValues} onChange={onCheckboxChange}>
        {option.children?.map((child) => (
          <SubOptionCheckbox child={child} />
        ))}
      </CheckboxGroup>
    </Box>
  );
};

interface SubOptionPopoverProps {
  option: Option;
  childValues: any;
  isLoading: boolean;
  onButtonClick: (option: Option) => void;
  onCheckboxChange: (values: string[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

const SubOptionPopover = ({
  option,
  childValues,
  isLoading,
  onButtonClick,
  onCheckboxChange,
  onOpen,
  onClose,
}: SubOptionPopoverProps) => {
  return (
    <FlexRow sx={popOverRow}>
      {option.children && (
        <PopoverComponent onOpen={onOpen} onClose={onClose}>
          <FlexRow sx={selectAllText}>
            <Button
              variant="unstyled"
              onClick={() => {
                onButtonClick(option);
              }}
            >
              <Text.Small color={Colors.TextQuiet}>Select all</Text.Small>
            </Button>
          </FlexRow>
          <SubOptionCheckboxGroup
            option={option}
            childValues={childValues}
            isLoading={isLoading}
            onCheckboxChange={onCheckboxChange}
          />
        </PopoverComponent>
      )}
    </FlexRow>
  );
};

interface UngroupedDropdownOptionProps {
  options: Options;
  checkedValues: any[];
  childValues: any;
  loading: boolean;
  handleParentCheckboxChange: (values: string[], isParent?: boolean) => void;
  handleSubOptionClick: (option: Option) => void;
  handleSubOptionCheckboxChange: (values: string[]) => void;
}

const UngroupedDropdownOptions = ({
  options,
  checkedValues,
  childValues,
  loading,
  handleParentCheckboxChange,
  handleSubOptionClick,
  handleSubOptionCheckboxChange,
}: UngroupedDropdownOptionProps) => {
  const [openPopovers, setOpenPopovers] = useState<Set<string>>(new Set());

  return (
    <CheckboxGroup onChange={handleParentCheckboxChange} value={checkedValues}>
      {options.map((option) => {
        const isPartiallySelected = isOptionPartiallySelected(option, childValues);
        const optionKey = String(option.value);
        const isChildOpen = openPopovers.has(optionKey);

        const handlePopoverOpen = () => {
          setOpenPopovers((prev) => new Set(prev).add(optionKey));
        };

        const handlePopoverClose = () => {
          setOpenPopovers((prev) => {
            const next = new Set(prev);
            next.delete(optionKey);

            return next;
          });
        };

        return (
          <Box
            key={`filter-dropdown-box-${option.value}`}
            sx={{
              ...optionContainer,
              // Maintain hover state when child filter is open
              ...(isChildOpen && {
                background: Colors.ArmyGreen100,
              }),
            }}
          >
            <OptionCheckbox option={option} isPartiallySelected={isPartiallySelected} />
            <SubOptionPopover
              option={option}
              childValues={childValues}
              isLoading={loading || false}
              onButtonClick={handleSubOptionClick}
              onCheckboxChange={handleSubOptionCheckboxChange}
              onOpen={handlePopoverOpen}
              onClose={handlePopoverClose}
            />
          </Box>
        );
      })}
    </CheckboxGroup>
  );
};

interface CheckboxDropdownOptionsProps {
  options: Options;
  checkedValues: (string | number)[];
  childValues?: string[];
  setChildValues?: (...args: any) => void;
  loading?: boolean;
  groupByPhase?: boolean;
  handleChange: (values: string[], isParent?: boolean) => void;
  selectAllChildren: (option: Option) => void;
}

const CheckboxDropdownOptions = ({
  options,
  checkedValues,
  childValues,
  setChildValues,
  loading,
  groupByPhase,
  handleChange,
  selectAllChildren,
}: CheckboxDropdownOptionsProps) => {
  if (!options) {
    return null;
  }

  const onSubOptionCheckboxChange = (values: string[]) => {
    setChildValues?.(values);
    handleChange(values, false);
  };

  if (!groupByPhase) {
    return (
      <UngroupedDropdownOptions
        options={options}
        checkedValues={checkedValues}
        childValues={childValues}
        loading={loading || false}
        handleParentCheckboxChange={handleChange}
        handleSubOptionClick={(option) => selectAllChildren(option)}
        handleSubOptionCheckboxChange={onSubOptionCheckboxChange}
      />
    );
  }

  return (
    <PhaseGroupedOptions
      options={options}
      checkedValues={checkedValues}
      onChange={handleChange}
      loading={loading}
      onChildrenChange={onSubOptionCheckboxChange}
      childValues={childValues}
    />
  );
};

interface FilterDropdownProps {
  options: Options;
  onChange?: (values: string[]) => void;
  onSubmit?: (values: string[], isParent: boolean) => void;
  onClearAll?: () => void;
  checkedValues: (string | number)[];
  setCheckedValues: (...args: any[]) => void;
  isExclusion?: boolean;
  exclusionValues?: string[];
  setExclusionValues?: (...args: any[]) => void;
  onExclusionChange?: (values: string[]) => void;
  onExclusionSubmit?: (values: string[], isParent: boolean) => void;
  onExclusionClearAll?: () => void;
  childValues?: string[];
  setChildValues?: (...args: any[]) => void;
  description?: string;
  children: React.ReactNode;
  setChildExclusionValues?: (...args: any[]) => void;
  exclusionChildValues?: string[];
  errorMessage?: string;
  loading?: boolean;
  groupByPhase?: boolean;
  maxHeight?: string;
  isFilterEnabled?: boolean;
  renderClearAll?: boolean;
  /** When true, options that are currently selected appear at the top of the list */
  sortSelectedFirst?: boolean;
}

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Isolates the search box in its own component so typing only re-renders this
 * small input — not the parent dropdown and its (potentially large) option
 * list. Only the debounced value is pushed up to the parent for filtering.
 */
const DebouncedSearchInput = ({
  onDebouncedChange,
}: {
  onDebouncedChange: (value: string) => void;
}) => {
  const [value, setValue] = useState('');
  const debouncedValue = useDebounce(value, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    onDebouncedChange(debouncedValue);
  }, [debouncedValue, onDebouncedChange]);

  return <SearchInput value={value} onChange={setValue} />;
};

const FilterDropdown = ({
  options,
  checkedValues,
  setCheckedValues,
  onChange,
  children,
  onSubmit,
  onClearAll,
  isExclusion,
  setExclusionValues,
  exclusionValues,
  onExclusionChange,
  onExclusionSubmit,
  exclusionChildValues,
  setChildExclusionValues,
  onExclusionClearAll,
  description,
  childValues,
  setChildValues,
  errorMessage,
  loading,
  groupByPhase,
  maxHeight,
  isFilterEnabled,
  renderClearAll = true,
  sortSelectedFirst = false,
}: FilterDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentOptions, setCurrentOptions] = useState(options);
  const [showExclusionOptions, setShowExclusionOptions] = useState(true);

  useEffect(() => {
    let filtered =
      options?.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ) ?? [];
    if (sortSelectedFirst && filtered.length > 0) {
      const checkedSet = new Set(checkedValues.map((v) => String(v)));
      filtered = [...filtered].sort((a, b) => {
        const aSelected = checkedSet.has(String(a.value));
        const bSelected = checkedSet.has(String(b.value));
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        return 0;
      });
    }
    setCurrentOptions(filtered);
  }, [searchTerm, options, sortSelectedFirst, checkedValues]);

  const filterCount = useMemo(() => {
    if (!options) {
      return '';
    }

    const checkedCount = checkedValues.filter(
      (value: string | number) => value !== undefined,
    ).length;
    const exclusionCount = exclusionValues
      ? exclusionValues.filter((value: string) => value !== undefined).length
      : 0;

    const totalCount = checkedCount + exclusionCount;

    const totalOptions =
      showExclusionOptions && isExclusion ? options.length * 2 : options.length;

    // If neither checkedValues nor exclusionValues are present, return an empty string
    if (totalCount === 0) {
      return '';
    }

    // If totalCount is not equal to totalOptions and is greater than 0, return totalCount
    if (totalCount !== totalOptions && totalCount > 0) {
      return totalCount.toString();
    }

    return 'All';
  }, [
    checkedValues,
    exclusionValues,
    showExclusionOptions,
    isExclusion,
    options?.length,
  ]);

  const handleClearAll = () => {
    setCheckedValues([]);
    if (setChildValues) {
      setChildValues([]);
    }
    if (onChange) {
      onChange([]);
    }
    if (onClearAll) {
      onClearAll();
    }
  };

  const handleClearAllExclusions = () => {
    setExclusionValues?.(DEFAULT_VERTICAL_EXCLUSIONS);
    if (setChildExclusionValues) {
      setChildExclusionValues([]);
    }
    if (onExclusionChange) {
      onExclusionChange([]);
    }
    if (onExclusionClearAll) {
      onExclusionClearAll();
    }
  };

  const handleChange = (values: string[], isParent = true) => {
    // remove duplicates from values
    const uniqueValues = Array.from(new Set(values)) as string[];
    setCheckedValues(uniqueValues);

    if (onSubmit) {
      onSubmit(uniqueValues, isParent);
    }
  };

  const selectAllChildren = (option: Option) => {
    const childrenValues: any = option.children?.map((child) => child?.value);
    // set existing and new values
    const newValues = Array.from(
      new Set([...(childValues ?? []), ...childrenValues]),
    ) as string[];
    setChildValues?.(newValues);
    handleChange(newValues, false);
  };

  return (
    <Dropdown title={children} filterCount={filterCount} errorMessage={errorMessage}>
      {description && (
        <DropdownItem sx={panelBox}>
          <Text sx={descriptionText}>{description}</Text>
        </DropdownItem>
      )}
      {isExclusion && (
        <DropdownItem sx={panelBox}>
          <Text sx={includeText}>include</Text>

          {renderClearAll && (
            <Button variant="unstyled" sx={noHeightButtonStyle} onClick={handleClearAll}>
              <Text sx={clearAllText}>Clear all</Text>
            </Button>
          )}
        </DropdownItem>
      )}
      <Box sx={checkboxContainerStyle(maxHeight)}>
        {isFilterEnabled && (
          <Box sx={{ margin: '0 12px', marginBottom: '8px' }}>
            <DebouncedSearchInput onDebouncedChange={setSearchTerm} />
          </Box>
        )}
        <CheckboxGroup
          defaultValue={checkedValues}
          value={checkedValues}
          onChange={(values) => {
            handleChange(values as string[], true);
          }}
        >
          <CheckboxDropdownOptions
            options={currentOptions}
            checkedValues={checkedValues}
            childValues={childValues}
            setChildValues={setCheckedValues}
            loading={loading}
            groupByPhase={groupByPhase}
            handleChange={handleChange}
            selectAllChildren={selectAllChildren}
          />
        </CheckboxGroup>
      </Box>
      {!isExclusion && renderClearAll && (
        <FlexRow sx={clearAllContainer}>
          <Button variant="unstyled" sx={noHeightButtonStyle} onClick={handleClearAll}>
            <Text.Small sx={clearAllText}>Clear all</Text.Small>
          </Button>
        </FlexRow>
      )}
      {isExclusion && (
        <>
          <FlexRow color="none" sx={exclusionRow}>
            {showExclusionOptions ? (
              <DropdownItem sx={panelBox}>
                <Text sx={{ ...includeText, color: 'brand.tangerine.900' }}>exclude</Text>

                <Button
                  variant="unstyled"
                  sx={noHeightButtonStyle}
                  onClick={handleClearAllExclusions}
                >
                  <Text sx={clearAllText}>Clear all</Text>
                </Button>
              </DropdownItem>
            ) : (
              <FlexRow
                sx={{ pb: (theme: any) => theme.space.eight }}
                onClick={() => setShowExclusionOptions(!showExclusionOptions)}
              >
                <Box sx={{ pr: 'eight' }}>
                  <Icon name={Icon.Name.Plus} fill={Colors.ForestGreen900} />
                </Box>
                <Text sx={exclusionText}>Add exclusion parameter</Text>
              </FlexRow>
            )}
          </FlexRow>

          {showExclusionOptions && (
            <ExclusionOptions
              options={options}
              groupByPhase={groupByPhase}
              isOptionPartiallySelected={(option) =>
                isOptionPartiallySelected(option, exclusionChildValues ?? [])
              }
              exclusionValues={exclusionValues}
              setExclusionValues={setExclusionValues}
              onExclusionSubmit={onExclusionSubmit}
              exclusionChildValues={exclusionChildValues}
              setChildExclusionValues={setChildExclusionValues}
              checkboxStyle={checkboxStyle}
              optionContainer={optionContainer}
              optionText={optionText()}
            />
          )}
        </>
      )}
    </Dropdown>
  );
};

export default FilterDropdown;
